import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, useMotionValue, animate } from "framer-motion";
import { toast } from "sonner";
import {
  Files, Download as DownloadIcon, HardDrive, Activity, UploadCloud, Crown,
} from "lucide-react";
import PageWrap from "@/fileshare/components/PageWrap";
import FilesTable from "@/fileshare/components/dashboard/FilesTable";
import { useAuth } from "@/fileshare/context/AuthContext";
import api from "@/fileshare/lib/api";
import { formatBytes, formatApiErrorDetail } from "@/fileshare/lib/format";
import { copyToClipboard } from "@/fileshare/lib/clipboard";
import { Button } from "@/fileshare/components/ui/button";
import { Skeleton } from "@/fileshare/components/ui/skeleton";

const STAT_SKELETONS = ["stat-a", "stat-b", "stat-c", "stat-d"];

function CountUp({ value }) {
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const controls = animate(mv, value, {
      duration: 1, ease: "easeOut",
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return controls.stop;
  }, [value, mv]);
  return <span>{display.toLocaleString()}</span>;
}

const StatCard = ({ icon: Icon, label, value, raw, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.4 }}
    className="bg-white border border-slate-200 rounded-lg p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
  >
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-500">{label}</span>
      <Icon className="h-4 w-4 text-slate-400" />
    </div>
    <p className="mt-3 text-2xl font-bold tracking-tight text-slate-900">
      {raw != null ? raw : <CountUp value={value} />}
    </p>
  </motion.div>
);

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const handledSession = useRef(null);
  const limit = 8;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, f] = await Promise.all([
        api.get("/stats/dashboard"),
        api.get(`/files/my-files?page=${page}&limit=${limit}`),
      ]);
      setStats(s.data);
      setFiles(f.data.files);
      setTotal(f.data.total);
    } catch (e) {
      console.error("Dashboard load failed:", e);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  // Stripe return polling (guarded by ref so each session_id is handled once)
  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId || handledSession.current === sessionId) return;
    handledSession.current = sessionId;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/checkout-status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Payment successful — your plan is upgraded!");
          await refreshUser();
          setParams({}, { replace: true });
          load();
          return;
        }
        if (data.status === "expired" || attempts >= 6) {
          toast.error("Payment not completed.");
          setParams({}, { replace: true });
          return;
        }
      } catch (e) {
        console.error("Checkout status poll failed:", e);
      }
      setTimeout(poll, 2000);
    };
    toast.loading("Confirming payment…", { id: "pay" });
    poll().finally(() => toast.dismiss("pay"));
    return () => { cancelled = true; };
  }, [params, refreshUser, setParams, load]);

  const copyLink = async (slug) => {
    const ok = await copyToClipboard(`${window.location.origin}/fileshares/d/${slug}`);
    if (ok) toast.success("Link copied");
    else toast.error("Could not copy — copy the link manually");
  };

  const del = async (id) => {
    try {
      await api.delete(`/files/${id}`);
      toast.success("File deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const pages = Math.ceil(total / limit) || 1;
  const storagePct = stats?.storage_limit ? Math.min(100, (stats.storage_used / stats.storage_limit) * 100) : 0;

  return (
    <PageWrap className="max-w-6xl mx-auto px-6 pt-12 pb-24">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Welcome, {user?.display_name}
          </h1>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
            Plan:
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium capitalize">
              {user?.tier !== "free" && <Crown className="h-3 w-3" />} {user?.tier}
            </span>
          </p>
        </div>
        <Link to="/fileshares"><Button className="bg-blue-500 hover:bg-blue-600 text-white gap-2"><UploadCloud className="h-4 w-4" /> Upload file</Button></Link>
      </div>

      {/* stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {loading && !stats ? (
          STAT_SKELETONS.map((k) => <Skeleton key={k} className="h-[92px] rounded-lg" />)
        ) : (
          <>
            <StatCard icon={Files} label="Total files" value={stats?.total_files || 0} delay={0} />
            <StatCard icon={Activity} label="Active files" value={stats?.active_files || 0} delay={0.05} />
            <StatCard icon={DownloadIcon} label="Total downloads" value={stats?.total_downloads || 0} delay={0.1} />
            <StatCard icon={HardDrive} label="Storage used" raw={formatBytes(stats?.storage_used || 0)} delay={0.15} />
          </>
        )}
      </div>

      {stats?.storage_limit && (
        <div className="mt-4 bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex justify-between text-xs text-slate-500 mb-2">
            <span>Storage</span>
            <span>{formatBytes(stats.storage_used)} / {formatBytes(stats.storage_limit)}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <motion.div className="h-full bg-blue-500" initial={{ width: 0 }} animate={{ width: `${storagePct}%` }} transition={{ duration: 0.8 }} />
          </div>
        </div>
      )}

      <FilesTable
        files={files}
        loading={loading}
        page={page}
        pages={pages}
        onPageChange={setPage}
        onCopy={copyLink}
        onDelete={del}
      />
    </PageWrap>
  );
}
